import { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { Serwist } from "@serwist/window";
import useSerwistUpdate from "@catchy/app-core/src/useSerwistUpdate";
import { setupSerwistNavigationCache } from "@catchy/app-core/src/serwistNavigationCache";
import useLocalStorage from "@/hooks/useLocalStorage";
import { initNativeStorageBridge } from "@/lib/nativeStorageBridge";
import { getStorageKey, migrateLegacyLocalStorageKeys } from "@/lib/regions/client";
import { isMaplibreSupported, useSafeAreaInsetsReady } from "@catchy/app-core";
import "@catchy/app-core/src/busFont.css";
import "@catchy/ui/styles/product.css";
import "@/styles/globals.css";
import "@/styles/maplibre.css";
if (typeof window !== "undefined") {
  initNativeStorageBridge();
}
function MyApp({
  Component,
  pageProps
}) {
  migrateLegacyLocalStorageKeys();
  const [isDark, setIsDark] = useState(false);
  const [mapsSupported, setMapsSupported] = useState(false);
  useEffect(() => {
    setIsDark(window.matchMedia("(prefers-color-scheme: dark)").matches);

    //window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (event) => { is correct but Safari < 14 sucks
    window.matchMedia("(prefers-color-scheme: dark)").addListener(event => setIsDark(event.matches));
  }, []);
  useEffect(() => {
    setMapsSupported(isMaplibreSupported());
  }, []);
  const [preferredServices, setPreferredServices] = useLocalStorage(getStorageKey("preferred_services"), []);
  const [preferredStopNames, setPreferredStopNames] = useLocalStorage(getStorageKey("preferred_stop_names"), []);
  const [preferSatellite, setPreferSatellite] = useLocalStorage(getStorageKey("prefer_satellite"), false);
  const [dismissedAlerts, setDismissedAlerts] = useLocalStorage(getStorageKey("dismissed_alerts"), []);
  const [dismissedAlertsLastCleaned, setDismissedAlertsLastCleaned] = useLocalStorage(getStorageKey("dismissed_alerts_last_cleaned"), null);
  useEffect(() => {
    const now = Date.now();
    dismissedAlerts?.length > 5 && now > dismissedAlertsLastCleaned + 604800000 && fetch(`/api/servicealerts/clean-dismissed-alerts?dismissedAlerts=${dismissedAlerts.join()}`).then(response => response.json()).then(result => {
      setDismissedAlerts(result);
    });
    !dismissedAlertsLastCleaned && setDismissedAlertsLastCleaned(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isApple, setIsApple] = useState(false);
  useEffect(() => {
    // Check if the device is an Apple device
    setIsApple(["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod", "MacIntel"].includes(navigator.platform) || navigator.userAgent.includes("Mac") && "ontouchend" in document);
  }, []);
  const router = useRouter();
  const isStartRoute = router.pathname === "/start";
  useEffect(() => {
    if (typeof window !== "undefined" && !sessionStorage.getItem("isInitialLoad")) {
      sessionStorage.setItem("isInitialLoad", "true");
    }
    const handleBeforeUnload = e => {
      const performanceEntries = performance.getEntriesByType("navigation");
      if (performanceEntries.length > 0 && performanceEntries[0].type === "reload") {
        return;
      }
      sessionStorage.setItem("isInitialLoad", "false");
    };
    const handleRouteChange = () => {
      sessionStorage.setItem("isInitialLoad", "false");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);
  const isSafeAreaInsetsSet = useSafeAreaInsetsReady(isStartRoute);
  useEffect(() => {
    const handleResize = () => {
      const vvh = window.visualViewport.height;
      document.documentElement.style.setProperty("--vvh", `${vvh}px`);
    };
    window.visualViewport.addEventListener("resize", handleResize);
    handleResize();
    return () => window.visualViewport.removeEventListener("resize", handleResize);
  }, []);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (process.env.NODE_ENV !== "production") {
      void Promise.all([navigator.serviceWorker.getRegistrations().then(registrations => Promise.all(registrations.map(registration => registration.unregister()))), "caches" in window ? caches.keys().then(keys => Promise.all(keys.map(key => caches.delete(key)))) : Promise.resolve()]).then(() => {
        if (navigator.serviceWorker.controller && sessionStorage.getItem("catchy_dev_sw_cleanup_reloaded") !== "true") {
          sessionStorage.setItem("catchy_dev_sw_cleanup_reloaded", "true");
          window.location.reload();
        }
      }).catch(error => console.error("Service worker cleanup failed:", error));
    }
  }, []);
  useSerwistUpdate({
    router,
    SerwistClass: Serwist,
    scriptUrl: `/sw.js?v=${process.env.NEXT_PUBLIC_APP_VERSION}`
  });
  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      sessionStorage.removeItem("catchy_dev_sw_cleanup_reloaded");
    }
  }, []);

  // The Pages Router event emitter is stable for the app lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setupSerwistNavigationCache(router, window), [router.events]);
  return isStartRoute ? isSafeAreaInsetsSet ? <Component {...pageProps} /> : null : <>
      <Head>
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="apple-mobile-web-app-title" content="Catchy" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="theme-color" content="#FFFFFF" media="(prefers-color-scheme: light)" />
        <meta name="theme-color" content="#000000" media="(prefers-color-scheme: dark)" />
        <meta name="application-name" content="Catchy" />
        <meta name="mobile-web-app-capable" content="yes" />
        <link rel="apple-touch-icon" sizes="180x180" href="/icons/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="alternate icon" href="/favicon.ico" />
        <link rel="mask-icon" href="/icons/mask-icon.svg" color="#ffc20e"></link>
      </Head>

      <Component isApple={isApple} dismissedAlerts={dismissedAlerts} isClient={isClient} isDark={isDark} mapsSupported={mapsSupported} preferredServices={preferredServices} preferredStopNames={preferredStopNames} preferSatellite={preferSatellite} setDismissedAlerts={setDismissedAlerts} setPreferredServices={setPreferredServices} setPreferredStopNames={setPreferredStopNames} setPreferSatellite={setPreferSatellite} {...pageProps} />
    </>;
}
export default MyApp;